import { useMemo, useState, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { JobService } from "@/services/job.service";
import { buildDispatchQueue, filterDispatchQueue, type DispatchFilter, type DispatchQueueItem } from "@/data/dispatchIntelligence";
import { useSchedulingDispatchServerData } from "@/hooks/useSchedulingDispatchServerPaging";
import { runAction, type ExecutableAction } from "@/lib/actionRunner";
import { errorPersistenceLog, startPersistenceLog, successPersistenceLog } from "@/lib/persistenceLogger";
import { OrderingModal } from "@/components/OrderingModal";
import { usePagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/PaginationBar";
import { SCHEDULING_DISPATCH_SERVER_PAGING_ENABLED } from "@/lib/dataRuntimeFlags";
import { isSchedulingDispatchServerPagedRoute } from "@/lib/routeScope";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { Job } from "@/data/types";
import {
  CockpitPage,
  CockpitMasthead,
  HeroMetric,
  HeroDivider,
  CockpitFilterBar,
  CockpitSearch,
  CockpitChipGroup,
  CockpitListShell,
  CockpitColumns,
  CockpitRow,
  CockpitEmpty,
  InlineMeta,
  StatusDot,
} from "@/components/cockpit/CockpitShell";

const FILTERS: { value: DispatchFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "blocked", label: "Blocked" },
  { value: "in_progress", label: "In progress" },
  { value: "payment_required", label: "Payment due" },
  { value: "ready_to_schedule", label: "Ready to schedule" },
  { value: "qa_ready", label: "QA ready" },
  { value: "stalled", label: "Stalled" },
];

const TEMPLATE = "96px minmax(210px,1.2fr) minmax(220px,1.2fr) 130px 170px 150px 24px";

function priorityTone(priority: DispatchQueueItem["priority"]): "good" | "warn" | "bad" | "muted" | "default" {
  if (priority === "critical") return "bad";
  if (priority === "high") return "warn";
  if (priority === "low") return "muted";
  return "default";
}

export default function DispatchIntelligenceCockpit({ embedded = false }: { embedded?: boolean } = {}) {
  const { jobs, issues, installerWrapRecords, installFollowUpTasks, setJobs } = useAppData();
  const navigate = useNavigate();
  const location = useLocation();
  const useServerPaging =
    SCHEDULING_DISPATCH_SERVER_PAGING_ENABLED &&
    isSchedulingDispatchServerPagedRoute(location.pathname, location.search);
  const [activeFilter, setActiveFilter] = useState<DispatchFilter>("all");
  const [search, setSearch] = useState("");
  const [orderingJob, setOrderingJob] = useState<Job | null>(null);
  const [orderingJobLoadingId, setOrderingJobLoadingId] = useState<string | null>(null);
  const [serverPage, setServerPage] = useState(0);
  const [serverPageSize, setServerPageSize] = useState(50);

  const fullQueue = useMemo(() => buildDispatchQueue(jobs, issues, installerWrapRecords, installFollowUpTasks), [jobs, issues, installerWrapRecords, installFollowUpTasks]);
  const serverData = useSchedulingDispatchServerData({
    enabled: useServerPaging,
    page: serverPage,
    pageSize: serverPageSize,
    search,
    filter: activeFilter,
  });
  const resolveDispatchAction = useCallback((item: DispatchQueueItem): ExecutableAction => {
    const isOrderCta = item.nextActionCta.toLowerCase().includes("order") && item.nextActionCta.toLowerCase().includes("material");
    if (isOrderCta) {
      const job = jobs.find((j) => j.id === item.jobId);
      if (job) return { label: item.nextActionCta, executionMode: "modal", openModal: () => setOrderingJob(job) };
    }
    return { label: item.nextActionCta, executionMode: "navigate", route: item.nextActionRoute };
  }, [jobs]);

  const handleDispatchCta = useCallback(async (item: DispatchQueueItem) => {
    const isOrderCta = item.nextActionCta.toLowerCase().includes("order") && item.nextActionCta.toLowerCase().includes("material");
    if (useServerPaging && isOrderCta) {
      const existingJob = jobs.find((j) => j.id === item.jobId);
      if (existingJob) {
        setOrderingJob(existingJob);
        return;
      }
      setOrderingJobLoadingId(item.jobId);
      const { data, error } = await JobService.getById(item.jobId);
      setOrderingJobLoadingId(null);
      if (error || !data) {
        toast.error("Could not load job", { description: "Open the job directly to place this material order." });
        navigate(item.nextActionRoute);
        return;
      }
      setOrderingJob(data);
      return;
    }
    const action = resolveDispatchAction(item);
    const handled = await runAction(action, { navigate });
    if (!handled) navigate(item.nextActionRoute);
  }, [jobs, navigate, resolveDispatchAction, useServerPaging]);

  const handleOrderingSubmit = useCallback(async (data: { jobId: string; vendor: string; orderType: "Batch" | "Direct" | "Rush"; notes: string }) => {
    const correlationId = startPersistenceLog("dispatch_order_submit", {
      entityType: "job",
      entityId: data.jobId,
      route: useServerPaging ? "/scheduling-dispatch" : "/dispatch-intelligence",
      vendor: data.vendor,
      orderType: data.orderType,
    });
    const activityEntry = {
      id: `dispatch-order-${Date.now()}`,
      date: new Date().toISOString(),
      action: `Materials ordered — ${data.vendor} (${data.orderType})`,
      user: "Dispatch",
      noteType: "ops" as const,
    };

    const currentJob = jobs.find((j) => j.id === data.jobId) ?? (orderingJob?.id === data.jobId ? orderingJob : null);
    const { data: updated, error } = await JobService.update(data.jobId, {
      materialReadiness: "Ordered" as any,
      planningStatus: "Ordered" as const,
      selectedVendor: data.vendor,
      activityHistory: (currentJob?.activityHistory ?? []).concat(activityEntry),
    });

    if (error || !updated) {
      errorPersistenceLog("dispatch_order_submit", correlationId, error ?? "No updated job returned", {
        entityType: "job",
        entityId: data.jobId,
      });
      toast.error("Order update failed", { description: "Could not persist dispatch update to DB." });
      return;
    }

    setJobs((prev) => prev.map((j) => (j.id === data.jobId ? updated : j)));
    setOrderingJob(null);
    if (useServerPaging) serverData.refresh();
    successPersistenceLog("dispatch_order_submit", correlationId, {
      entityType: "job",
      entityId: data.jobId,
    });
    toast.success("Order placed", { description: `${data.vendor} — ${data.orderType}` });
  }, [jobs, orderingJob, serverData, setJobs, useServerPaging]);

  const clientFiltered = useMemo(() => {
    let q = filterDispatchQueue(fullQueue, activeFilter);
    if (search.trim()) {
      const s = search.toLowerCase();
      q = q.filter((item) => item.customerName.toLowerCase().includes(s) || item.jobDisplayRef.toLowerCase().includes(s) || item.nextAction.toLowerCase().includes(s));
    }
    return q;
  }, [fullQueue, activeFilter, search]);

  const clientPagination = usePagination(clientFiltered, 50);
  useEffect(() => {
    if (useServerPaging) {
      setServerPage(0);
      return;
    }
    clientPagination.reset();
  }, [search, activeFilter, useServerPaging]); // eslint-disable-line react-hooks/exhaustive-deps

  const serverTotalPages = Math.max(1, Math.ceil(serverData.count / serverPageSize));
  const serverSafePage = Math.min(serverPage, serverTotalPages - 1);
  useEffect(() => {
    if (useServerPaging && serverSafePage !== serverPage) setServerPage(serverSafePage);
  }, [serverPage, serverSafePage, useServerPaging]);

  const serverPagination = useMemo(() => ({
    paged: serverData.rows,
    page: serverSafePage,
    totalPages: serverTotalPages,
    totalItems: serverData.count,
    setPage: setServerPage,
    hasNext: serverSafePage < serverTotalPages - 1,
    hasPrev: serverSafePage > 0,
    next: () => setServerPage((p) => Math.min(p + 1, serverTotalPages - 1)),
    prev: () => setServerPage((p) => Math.max(p - 1, 0)),
    reset: () => setServerPage(0),
    pageStart: serverData.count === 0 ? 0 : serverSafePage * serverPageSize + 1,
    pageEnd: Math.min((serverSafePage + 1) * serverPageSize, serverData.count),
    pageSize: serverPageSize,
    setPageSize: (size: number) => {
      setServerPageSize(size);
      setServerPage(0);
    },
  }), [serverData.count, serverData.rows, serverPageSize, serverSafePage, serverTotalPages]);

  const filtered = useServerPaging ? serverData.rows : clientFiltered;
  const pagination = useServerPaging ? serverPagination : clientPagination;

  const kpis = useMemo(() => ({
    critical: useServerPaging ? (serverData.summary?.critical ?? 0) : fullQueue.filter((q) => q.priority === "critical").length,
    blocked: useServerPaging ? (serverData.summary?.blocked ?? 0) : fullQueue.filter((q) => q.blockerReason !== null).length,
    paymentDue: useServerPaging ? (serverData.summary?.paymentDue ?? 0) : fullQueue.filter((q) => q.state.payment === "deposit_pending" || q.state.payment === "payment_pending" || q.state.payment === "payment_deferred").length,
    stalled: useServerPaging ? (serverData.summary?.stalled ?? 0) : fullQueue.filter((q) => q.flags.includes("Stalled")).length,
    total: useServerPaging ? (serverData.summary?.total ?? serverData.count) : fullQueue.length,
  }), [fullQueue, serverData.count, serverData.summary, useServerPaging]);

  const chipOptions = useMemo(() => FILTERS.map((f) => {
    let count = 0;
    if (useServerPaging) {
      count = f.value === "all" ? (serverData.summary?.total ?? serverData.count) :
        f.value === "blocked" ? (serverData.summary?.blocked ?? 0) :
        f.value === "in_progress" ? (serverData.summary?.inProgress ?? 0) :
        f.value === "payment_required" ? (serverData.summary?.paymentDue ?? 0) :
        f.value === "ready_to_schedule" ? (serverData.summary?.readyToSchedule ?? 0) :
        f.value === "qa_ready" ? (serverData.summary?.qaReady ?? 0) :
        f.value === "stalled" ? (serverData.summary?.stalled ?? 0) :
        0;
    } else {
      count = f.value === "all" ? kpis.total : filterDispatchQueue(fullQueue, f.value).length;
    }
    return { value: f.value, label: f.label, count };
  }), [fullQueue, kpis.total, serverData.count, serverData.summary, useServerPaging]);

  const content = (
    <CockpitPage className="px-0 py-0 max-w-none">
      {!embedded && (
      <CockpitMasthead
        // eyebrow="Execution · Dispatch queue"
        title="Dispatch Intelligence"
        right={(
          <>
            <HeroMetric label="Critical" value={kpis.critical} hero tone={kpis.critical > 0 ? "bad" : "default"} />
            <HeroDivider />
            <HeroMetric label="Blocked" value={kpis.blocked} tone={kpis.blocked > 0 ? "warn" : "default"} />
            <HeroMetric label="Payment due" value={kpis.paymentDue} />
            <HeroMetric label="Stalled" value={kpis.stalled} />
          </>
        )}
      />
    )}

  {embedded && (
        // Slim KPI strip — preserves the Critical/Blocked/Payment due/Stalled
        // glanceable counts that the masthead used to carry, without a second
        // page title competing with the parent "Scheduling" header.
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-2.5 border-y border-border/60 bg-muted/10">
          <HeroMetric label="Critical" value={kpis.critical} tone={kpis.critical > 0 ? "bad" : "default"} />
          <HeroDivider />
          <HeroMetric label="Blocked" value={kpis.blocked} tone={kpis.blocked > 0 ? "warn" : "default"} />
          <HeroDivider />
          <HeroMetric label="Payment due" value={kpis.paymentDue} />
          <HeroDivider />
          <HeroMetric label="Stalled" value={kpis.stalled} />
        </div>
      )}

      <CockpitFilterBar
        search={<CockpitSearch value={search} onChange={setSearch} placeholder="Search jobs, customers, actions" />}
        chips={<CockpitChipGroup value={activeFilter} onChange={(v) => setActiveFilter(v as DispatchFilter)} options={chipOptions} />}
      />

      <CockpitListShell>
        <CockpitColumns template={TEMPLATE}>
          <span>Priority</span><span>Job</span><span>State</span><span>Stage</span><span>Next action</span><span /><span />
        </CockpitColumns>
        {filtered.length === 0 ? <CockpitEmpty message={serverData.loading ? "Loading dispatch jobs..." : activeFilter === "all" ? "No active dispatch jobs." : "No jobs match this dispatch view."} /> : pagination.paged.map((item) => (
          <CockpitRow key={item.jobId} template={TEMPLATE} affordance={false}>
            <div className="flex items-center gap-2"><StatusDot tone={priorityTone(item.priority)} /><p className="text-xs uppercase text-muted-foreground font-medium">{item.priority}</p></div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{item.jobDisplayRef} · {item.customerName}</p>
              <InlineMeta items={[item.address]} />
            </div>
            <div className="min-w-0"><InlineMeta items={[item.installLabel, item.paymentLabel]} />{item.blockerReason && <p className="text-xs text-destructive truncate mt-1">{item.blockerReason}</p>}</div>
            <p className="text-xs text-muted-foreground truncate">{item.currentStage}</p>
            <div className="min-w-0"><p className="text-sm text-foreground truncate">{item.nextAction}</p><div className="flex flex-wrap gap-1 mt-1">{item.flags.slice(0, 2).map((flag) => <Badge key={flag} variant="outline" className="text-[9px] h-4 px-1.5">{flag}</Badge>)}</div></div>
            <Button size="sm" variant={item.priority === "critical" ? "destructive" : "default"} className="h-8 text-xs" disabled={orderingJobLoadingId === item.jobId} onClick={() => handleDispatchCta(item)}>{orderingJobLoadingId === item.jobId ? "Loading..." : item.nextActionCta}</Button>
          </CockpitRow>
        ))}
      </CockpitListShell>

      <PaginationBar {...pagination} />

      {orderingJob && <OrderingModal open={!!orderingJob} onOpenChange={(open) => { if (!open) setOrderingJob(null); }} job={orderingJob} onSendToOrdering={handleOrderingSubmit} />}
    </CockpitPage>
  );

  return embedded ? content : <CockpitPage className="px-0 py-0 max-w-none">{content}</CockpitPage>;
}
